import { getLanguage } from './config';
import { assertLatinDigits } from './clinicalSafety';

/**
 * Locale-aware formatters.
 *
 * Two families deliberately exist:
 *
 *  - `formatLocale*` follows the UI language. Use it on patient-facing surfaces, where a
 *    Telugu month name is genuinely easier to read.
 *  - `formatClinical*` is pinned to `en-IN` whatever the UI language. Use it for anything
 *    that lands on a printed or shareable medical document — prescriptions, lab reports,
 *    bills, token slips, discharge summaries — so the date on the paper matches the date
 *    in the record, in the pharmacy's system and in the lab's report.
 *
 * Both always produce Latin digits; `assertLatinDigits` fails loudly in dev if that ever
 * stops being true.
 */

const CLINICAL_LOCALE = 'en-IN-u-nu-latn';

function localeFor(languageCode: string): string {
  return getLanguage(languageCode).locale;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatLocaleDate(
  value: string | number | Date | null | undefined,
  languageCode: string,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' },
): string {
  const d = toDate(value);
  if (!d) return '—';
  return assertLatinDigits(
    new Intl.DateTimeFormat(localeFor(languageCode), options).format(d),
    'formatLocaleDate',
  );
}

export function formatLocaleDateTime(
  value: string | number | Date | null | undefined,
  languageCode: string,
): string {
  const d = toDate(value);
  if (!d) return '—';
  return assertLatinDigits(
    new Intl.DateTimeFormat(localeFor(languageCode), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d),
    'formatLocaleDateTime',
  );
}

/** Date for a printed or shareable medical document. Always `en-IN`, whatever the UI language. */
export function formatClinicalDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(CLINICAL_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

export function formatClinicalDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(CLINICAL_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

export function formatLocaleNumber(
  value: number | null | undefined,
  languageCode: string,
  options?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return assertLatinDigits(
    new Intl.NumberFormat(localeFor(languageCode), options).format(value),
    'formatLocaleNumber',
  );
}

/**
 * Money is always Indian-grouped Latin digits with a ₹ sign, in every language. Currency is
 * one of the things a patient reads back to a receptionist, so the shape must not shift.
 */
export function formatLocaleCurrency(
  value: number | null | undefined,
  languageCode: string,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return assertLatinDigits(
    new Intl.NumberFormat(localeFor(languageCode), {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(value),
    'formatLocaleCurrency',
  );
}

/**
 * A measured clinical value: vital sign, dose, lab result. The number and its unit are
 * rendered verbatim in Latin digits with the unit untranslated, in every language.
 */
export function formatMeasurement(
  value: number | string | null | undefined,
  unit?: string | null,
): string {
  if (value === null || value === undefined || value === '') return '—';
  return unit ? `${value} ${unit}` : String(value);
}
